import { Home, Play, UtensilsCrossed, Brain, Check, ArrowRight } from 'lucide-react';
import { pushToDataLayer } from '../utils/gtm';

export default function HowItWorks() {
  const steps = [
    {
      number: 1,
      icon: Home,
      title: 'Start von zuhause – ohne Equipment',
      description: 'Du brauchst nur ein Handtuch, Wasser und 1–2 m Platz. Die Workouts funktionieren im Wohnzimmer, Schlafzimmer oder auf Reisen.',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200'
    },
    {
      number: 2,
      icon: Play,
      title: 'Ein 30-Minuten-Video pro Tag',
      description: 'Siggi führt dich Schritt für Schritt durch Warm-up, Hauptteil, Cool-down und Bauch-Boost. Kein Planen, kein Nachdenken – einfach auf Play drücken und mitmachen.',
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-200'
    },
    {
      number: 3,
      icon: UtensilsCrossed,
      title: 'Ernährungspläne, die sofort funktionieren',
      description: 'Normal, vegetarisch oder vegan. Mit Einkaufslisten, Rezepten und klaren Tagesanleitungen.',
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      borderColor: 'border-green-200'
    },
    {
      number: 4,
      icon: Brain,
      title: 'Mini-Mindset-Impulse für echte Veränderung',
      description: 'Tägliche kurze Coachings, die deinen inneren Autopiloten neu programmieren – für bessere Entscheidungen ohne Druck oder Disziplinkampf.',
      gradient: 'from-amber-500 to-amber-600',
      bgGradient: 'from-amber-50 to-amber-100',
      borderColor: 'border-amber-200'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            ⭐ So funktioniert es
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            So funktioniert der 7-Tage-Body-Boost
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-4">
            Du bekommst nicht nur 7 Trainingsvideos – sondern ein komplettes Coaching, das Körper und Kopf in nur einer Woche neu ausrichtet.
          </p>
          <p className="text-lg font-semibold text-green-600">
            Alles zuhause, ohne Geräte, ohne Aufwand.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {steps.map((step) => (
            <div
              key={step.number}
              className={`relative bg-gradient-to-br ${step.bgGradient} rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-2 ${step.borderColor}`}
            >
              <div className="flex items-start gap-6">
                <div className={`relative flex-shrink-0`}>
                  <div className={`w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className={`absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br ${step.gradient} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                    {step.number}
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 relative overflow-hidden bg-gradient-to-br from-orange-600 via-orange-500 to-red-600 rounded-3xl p-8 lg:p-12 text-white shadow-2xl border-4 border-orange-400">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />

          <div className="max-w-3xl mx-auto relative z-10">
            <h3 className="text-3xl lg:text-4xl font-extrabold mb-6 leading-tight text-center">
              Sichere dir jetzt deinen Platz
            </h3>

            <a
              href="https://www.digistore24.com/product/642539/siebentage5kilo/?ds24_domain=www.digistore24.com&voucher=7TageStart"
              className="group relative inline-block w-full"
              onClick={() => pushToDataLayer('initiate_checkout')}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-gradient-to-r from-white to-yellow-50 text-orange-600 px-10 py-6 rounded-2xl font-black text-xl lg:text-2xl hover:from-yellow-50 hover:to-white transition-all shadow-2xl transform group-hover:scale-105 group-hover:-translate-y-1 duration-300 flex items-center justify-center gap-3">
                <span>Jetzt starten</span>
                <ArrowRight className="w-7 h-7 group-hover:translate-x-2 transition-transform" />
              </div>
            </a>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span>Sofortiger Zugang</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span>100€ Gespart</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
